import React, {FC} from 'react';
import {useQuery} from '@tanstack/react-query';
import parseISO from 'date-fns/parseISO';
import {useNavigate} from 'react-router-dom';
import useMe from '../../../hooks/useMe';
import {Glum} from '../../../puddleglum';
import {AppointmentDate, USDollar} from '../../../utils/format';
import RowActionMenu from 'components/RowActionMenu';
import {
	Badge,
	Box,
	Button,
	Heading,
	HStack,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useBreakpointValue,
	VStack,
} from 'components/ui/chakra';
import PendingBillingStatementController from 'puddleglum/api/patient/PendingBillingStatementController';

const PendingBillingStatements: FC = () => {
	const navigate = useNavigate();
	const {me} = useMe();

	const {data: billingStatements} = useQuery(['pending-billing-statements'], async () => {
		const {data} = await PendingBillingStatementController.index();
		return data;
	});

	const navigateToBillPayScreen = (billingStatement: Glum.Models.BillingStatement) => {
		navigate('/pay-bill', {
			state: {
				billingStatement,
			},
		});
	};

	const isMobile = useBreakpointValue({base: true, md: false});

	if (!billingStatements || billingStatements.length === 0) return null;

	// Mobile card view
	if (isMobile) {
		return (
			<Box w='full'>
				<Heading mb={4} size='md' color='gray.500'>
					Pending Bills
				</Heading>
				<VStack spacing={3} w='full'>
					{billingStatements.map((billingStatement) => {
						const isPharmacy = !!billingStatement.pharmacy_id;
						const orgName =
							billingStatement.organization?.name || billingStatement.pharmacy?.name;
						const phone =
							billingStatement.organization?.phone ||
							billingStatement.pharmacy?.phone;
						const isForDependent =
							billingStatement.user && billingStatement.user.id !== me?.id;

						return (
							<Box
								key={billingStatement.id}
								w='full'
								p={4}
								bgColor='yellow.50'
								borderRadius='md'
								border='solid 1px'
								borderColor='yellow.500'
							>
								<VStack align='stretch' spacing={3}>
									<HStack justify='space-between' align='start'>
										<Text fontSize='sm' color='gray.500' fontWeight='medium'>
											{AppointmentDate(
												parseISO(billingStatement.created_at!),
											)}
										</Text>
										<Text fontSize='lg' fontWeight='bold' color='gray.700'>
											{USDollar.format(
												Number(billingStatement.employee_price),
											)}
										</Text>
									</HStack>

									<Box>
										{isForDependent && (
											<Badge colorScheme='blue' fontSize='xs' mb={1}>
												{billingStatement.user!.first_name}{' '}
												{billingStatement.user!.last_name}
											</Badge>
										)}
										<Text fontSize='md' fontWeight='semibold'>
											{orgName}
										</Text>
										<Text fontSize='sm' color='gray.500'>
											{isPharmacy ? 'Pharmacy' : 'Provider'}
										</Text>
									</Box>

									<HStack spacing={2}>
										<Button
											size='sm'
											colorScheme='blue'
											onClick={() =>
												navigateToBillPayScreen(billingStatement)
											}
											flex={1}
										>
											Pay Now
										</Button>
										<Button
											size='sm'
											variant='outline'
											as='a'
											href={`tel:${phone}`}
											flex={1}
										>
											Call
										</Button>
									</HStack>
								</VStack>
							</Box>
						);
					})}
				</VStack>
			</Box>
		);
	}

	// Desktop table view
	return (
		<Box w='full'>
			<Heading mb={4} size='md' color='gray.500'>
				Pending Bills
			</Heading>
			<Box
				overflowX='auto'
				bgColor='white'
				borderRadius='md'
				border='solid 1px'
				borderColor='gray.200'
			>
				<Table size='sm'>
					<Thead bgColor='gray.50'>
						<Tr>
							<Th>Date</Th>
							<Th>Provider/Pharmacy</Th>
							<Th isNumeric>Amount Due</Th>
							<Th>Actions</Th>
						</Tr>
					</Thead>
					<Tbody>
						{billingStatements.map((billingStatement) => {
							const isPharmacy = !!billingStatement.pharmacy_id;
							const orgName =
								billingStatement.organization?.name ||
								billingStatement.pharmacy?.name;
							const phone =
								billingStatement.organization?.phone ||
								billingStatement.pharmacy?.phone;
							const isForDependent =
								billingStatement.user && billingStatement.user.id !== me?.id;

							return (
								<Tr
									key={billingStatement.id}
									bgColor='yellow.50'
									_hover={{bgColor: 'yellow.100'}}
								>
									<Td>
										<Text fontSize='sm' fontWeight='medium'>
											{AppointmentDate(
												parseISO(billingStatement.created_at!),
											)}
										</Text>
									</Td>
									<Td>
										<VStack align='start' spacing={0}>
											{isForDependent && billingStatement.user && (
												<Badge colorScheme='blue' fontSize='xs' mb={1}>
													{billingStatement.user.first_name}{' '}
													{billingStatement.user.last_name}
												</Badge>
											)}
											<Text fontSize='sm' fontWeight='medium' noOfLines={2}>
												{orgName}
											</Text>
											<Text fontSize='xs' color='gray.500'>
												{isPharmacy ? 'Pharmacy' : 'Provider'}
											</Text>
										</VStack>
									</Td>
									<Td isNumeric>
										<Text fontSize='md' fontWeight='bold' color='gray.700'>
											{USDollar.format(
												Number(billingStatement.employee_price),
											)}
										</Text>
									</Td>
									<Td>
										<RowActionMenu
											ariaLabel={`Actions for bill ${billingStatement.id}`}
											items={[
												{
													label: 'Pay now',
													onClick: () =>
														navigateToBillPayScreen(billingStatement),
												},
												{
													label: 'Call',
													onClick: () => {
														window.location.href = `tel:${phone}`;
													},
												},
											]}
										/>
									</Td>
								</Tr>
							);
						})}
					</Tbody>
				</Table>
			</Box>
		</Box>
	);
};

export default PendingBillingStatements;
